import { Button, Header, Message } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import { RegieContext } from '@/contexts';
import type { CycleType } from '@/types';
import {
  getCycleSelectionErrors,
  getRelancesAvantEcheanceDisabled,
  getRelancesProlongeeDisabled,
  getTransfertDisabled,
} from '@/utils/cycle';
import { CyclesListe } from '../Cycle';

type SelectionCyclesImpayesProps = {
  selectedItems?: CycleType[];
  onItemSelect?: (items: CycleType[]) => void;
  onTransfertClick?: () => void;
  onRelanceAvantClick?: () => void;
  onRelanceProlongeeClick?: () => void;
};

const SelectionCyclesImpayes = (props: SelectionCyclesImpayesProps) => {
  const {
    selectedItems,
    onItemSelect,
    onTransfertClick,
    onRelanceAvantClick,
    onRelanceProlongeeClick,
  } = props;

  const { regies, selectedRegie } = React.useContext(RegieContext);
  const regie = React.useMemo(
    () => _.find(regies, { identifiant: selectedRegie }),
    [regies, selectedRegie],
  );

  /** On désactive le bouton si on a pas de cycle sélectionné
   *  et si tous les cycles sélectionnés n'ont pas la même dateLimite / app source
   *  et si la dateLimite + jours de régie prolongé n'est pas dépassée au jour J */
  const transfertDisabled = React.useMemo(
    () => getTransfertDisabled(selectedItems, regie),
    [selectedItems, regie],
  );

  /** Relances AVANT la date limite de paiement : le bouton n'est actif que si
   *  tous les cycles sélectionnés ont une date limite supérieure ou égale au jour J. */
  const relancesAvantDisabled = React.useMemo(
    () => getRelancesAvantEcheanceDisabled(selectedItems),
    [selectedItems],
  );

  /** Relances de la régie prolongée (APRÈS la date limite) : le bouton n'est actif
   *  que si tous les cycles sélectionnés ont une date limite dépassée mais une date
   *  limite prolongée non dépassée au jour J. */
  const relancesProlongeeDisabled = React.useMemo(
    () => getRelancesProlongeeDisabled(selectedItems, regie?.joursEncaissementSupplementaires),
    [selectedItems, regie?.joursEncaissementSupplementaires],
  );

  const errors = React.useMemo<string[]>(
    () => getCycleSelectionErrors(transfertDisabled, ''),
    [transfertDisabled],
  );

  const handleSelect = React.useCallback(
    (item, selected) => {
      onItemSelect(selected);
    },
    [onItemSelect],
  );

  const handleTransfertClick = React.useCallback(() => {
    onTransfertClick();
  }, [onTransfertClick]);

  const handleRelanceAvantClick = React.useCallback(() => {
    onRelanceAvantClick();
  }, [onRelanceAvantClick]);

  const handleRelanceProlongeeClick = React.useCallback(() => {
    onRelanceProlongeeClick();
  }, [onRelanceProlongeeClick]);

  return (
    <div>
      <Header as="h2" style={{ margin: 0, paddingLeft: '1em' }}>
        <Header.Content>Cycles de facturation</Header.Content>
      </Header>
      <CyclesListe
        type="impayes"
        height={425}
        onSelect={handleSelect}
        selectedItems={selectedItems}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '4em',
          paddingRight: '1em',
        }}
      >
        <Button
          data-testid="button-relance-avant-echeance"
          size="large"
          positive
          content="Accéder aux relances"
          disabled={relancesAvantDisabled !== ''}
          onClick={handleRelanceAvantClick}
          style={{ marginRight: '1em' }}
        />
        {regie?.regieProlongee && (
          <Button
            data-testid="button-relance-prolongee"
            size="large"
            positive
            content="Accéder aux relances de la régie prolongée"
            disabled={relancesProlongeeDisabled !== ''}
            onClick={handleRelanceProlongeeClick}
            style={{ marginRight: '1em' }}
          />
        )}
        <Button
          data-testid="button-transfert-impayes"
          size="large"
          positive
          content="Accéder aux transferts"
          disabled={transfertDisabled !== ''}
          onClick={handleTransfertClick}
        />
      </div>
      {!_.isEmpty(errors) && (
        <Message warning style={{ marginTop: '1em' }}>
          <Message.List>
            {_.map(errors, (e, i) => (
              <Message.Header key={i}>{e}</Message.Header>
            ))}
          </Message.List>
        </Message>
      )}
    </div>
  );
};

export default SelectionCyclesImpayes;
